/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  isNotNull,
  mapToResource,
  ResourceRepository,
  ResourceSearchService,
  SearchResourceServiceConfig,
} from '@common/tech-shared';
import { VorgangResource, VorgangService, VorgangWithEingangLinkRel } from '@alfa-client/vorgang-shared';
import {
  OrganisationsEinheitListResource,
  OrganisationsEinheitResource,
  OrganisationsEinheitService,
  ZUSTAENDIGE_STELLE_SERVICE,
  ZustaendigeStelleService,
} from '@alfa-client/zustaendige-stelle-shared';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { getUrl, ResourceUri } from '@ngxp/rest';
import { Observable, tap } from 'rxjs';

@Component({
  selector: 'alfa-search-organisations-einheit-container',
  templateUrl: './search-organisations-einheit-container.component.html',
  providers: [
    {
      provide: ZUSTAENDIGE_STELLE_SERVICE,
      useFactory: createOrganisationEinheitService,
      deps: [ResourceRepository, VorgangService],
    },
  ],
  standalone: false,
})
export class SearchOrganisationsEinheitContainerComponent implements OnInit, OnDestroy {
  private readonly service = inject<ZustaendigeStelleService<OrganisationsEinheitResource>>(ZUSTAENDIGE_STELLE_SERVICE);

  @Input() public fieldControl: AbstractControl<ResourceUri>;

  public organisationsEinheitResource$: Observable<OrganisationsEinheitResource>;

  ngOnInit(): void {
    this.organisationsEinheitResource$ = this.getSelectedResult();
  }

  ngOnDestroy(): void {
    this.service.clearSelectedResult();
  }

  getSelectedResult(): Observable<OrganisationsEinheitResource> {
    return this.service
      .getSelectedResult()
      .pipe(tap((organisationsEinheitResource: OrganisationsEinheitResource) => this.handleResult(organisationsEinheitResource)));
  }

  handleResult(organisationsEinheitResource: OrganisationsEinheitResource): void {
    if (isNotNull(organisationsEinheitResource)) {
      this.fieldControl.patchValue(getUrl(organisationsEinheitResource));
    }
  }
}

export function createOrganisationEinheitService(
  resourceRepository: ResourceRepository,
  vorgangService: VorgangService,
): OrganisationsEinheitService {
  return new OrganisationsEinheitService(createSearchService(resourceRepository, vorgangService));
}

function createSearchService(
  resourceRepository: ResourceRepository,
  vorgangService: VorgangService,
): ResourceSearchService<VorgangResource, OrganisationsEinheitListResource, OrganisationsEinheitResource> {
  return new ResourceSearchService<VorgangResource, OrganisationsEinheitListResource, OrganisationsEinheitResource>(
    buildConfig(vorgangService),
    resourceRepository,
  );
}

function buildConfig(vorgangService: VorgangService): SearchResourceServiceConfig<VorgangResource> {
  return {
    baseResource: vorgangService.selectVorgangWithEingang().pipe(mapToResource<VorgangResource>()),
    searchLinkRel: VorgangWithEingangLinkRel.SEARCH_ORGANISATIONS_EINHEIT,
  };
}
